import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Operaciones } from '../api/operaciones';
import type { EvaluacionPuerta, MotivoEvaluacion } from '../api/ops/clientes';
import type { DetalleGrupoDeAcceso, GrupoDeAcceso, MotivoLectura, ResultadoValidacion } from '../api/ops/pases';
import type { PersonaDeLaTablet } from '../api/ops/tablet';
import { ErrorApi } from '../api/problema';
import { Icono } from '../componentes/Icono';
import { permisosDe, type Permisos } from '../permisos/permisos';
import type { SesionMiembro } from './almacenTablet';
import { Camara } from './Camara';
import { cupoSinConexion, dejaDentro, elementoDeLectura, esLectura, validarSinConexion, type Copia, type ResultadoLocal } from './copiaPuerta';
import { avisoSinConexion, TEXTOS_SIN_CONEXION, textoFranjaSinConexion } from './sinConexion';
import { Teclado } from './Teclado';
import { COLOR, explicar, lineaCorta, MOTIVOS_EVALUACION, NIVELES_EVALUACION, NO_ES_TUYA, PRESETS_ADMITIR, T_PUERTA, type DatosLectura } from './textosPuerta';
import { useCopia } from './useCopia';
import { textoSinContacto, TITULO_SIN_CONTACTO } from '../pantallas/taquilla/venta';
import './puerta.css';

/**
 * La Puerta en la tablet (6.1 y 6.2), con la sesión con PIN de quien está en la puerta (D-067):
 *
 * - Lee con la cámara (`qr`), el lector de pistola (`qr`, teclea la dirección y Enter), el código tecleado (`typed_code`) o
 *   la búsqueda por nombre (`listPasses` con `q`, luego `name_search`); `validatePass` responde verde, ámbar o rojo con
 *   cada `ScanReason` en palabras. Un rojo es una respuesta, no un error. Al profesional, un `403` por el ámbito «lo suyo»
 *   se pinta «No es una cita tuya» (G415).
 * - Ámbar: «Admitir igualmente» solo si `admissible` y la persona tiene `pases.puerta.admitir_ambar` (con motivo); si no,
 *   «Solo un encargado puede admitirlo igualmente» y lo decide un encargado con **su PIN en esta tablet**, solo para esa
 *   decisión (G783). «Rechazar» siempre (`decideAmberScan`).
 * - Detalle: los datos de puerta del pase, «Deshacer la llegada» (`undoArrival`, con motivo, `pases.llegadas.deshacer` y
 *   solo con conexión; su fila sale sin color, G416, G421) y la evaluación de puerta (G193): `getPass` da el `customer_id`,
 *   la vigente con `listDoorAssessments` y `createDoorAssessment` con `pass_ref`.
 * - «Dentro» con `getAccessGroup` (`attendance`), «Últimas lecturas» de esta tablet y el grupo de `listAccessGroups`.
 * - Sin conexión (6.2): valida con la copia cifrada (`useCopia`), con la franja «Última conexión… hasta…», «Conéctate para
 *   no perder lo leído» 2 h antes del tope (G784) y «Sesión caducada» pasado (G417, G423, G424); lo leído se envía al volver.
 * - Taquilla: lo que queda de la parte del cupo de esta tablet (`box_office_quota − box_office_used`) y «Abrir la
 *   taquilla» (10.7 en modo tablet, la pantalla de Eventos).
 */
export interface PropsPuerta {
    /** Operaciones con la sesión con PIN de la persona. */
    ops: Operaciones;
    /** Operaciones con la credencial de la tablet (la copia y «¿Quién eres?» del encargado). */
    opsTablet: Operaciones;
    /** Operaciones con otra sesión (la del encargado que admite un ámbar con su PIN, G783). */
    crearOps: (obtenerToken: () => string | null) => Operaciones;
    negocio: string;
    nombreNegocio: string;
    nombreTablet: string;
    sesion: SesionMiembro;
    /** Zona horaria del negocio (de «¿Quién eres?»). */
    zona: string | null;
    onCambiarPersona: () => void;
    /** «Abrir la taquilla» (10.7 en modo tablet). */
    onTaquilla: (grupo: GrupoDeAcceso) => void;
    ahora?: () => Date;
    /** Con conexión o sin ella (por defecto, lo que dice el navegador). */
    conexion?: boolean;
    almacenCopia?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

interface PaseVisto {
    id: string | null;
    codigo: string | null;
    nombre: string | null;
    tipo: string | null;
    lista: string | null;
    rrpp: string | null;
    zona: string | null;
    notas: string | null;
    acompanantes: number;
    desde: string | null;
    hasta: string | null;
}

interface Resultado {
    color: 'green' | 'amber' | 'red';
    motivo: MotivoLectura | 'not_own';
    titulo: string;
    texto: string;
    pase: PaseVisto | null;
    lectura: string | null;
    admisible: boolean;
    sinConexion: boolean;
    /** Sin conexión: la lectura local, para anotarla al decidir el ámbar. */
    local?: { r: ResultadoLocal; entrada: { qr: string } | { codigo: string }; cuando: Date };
    decidido?: boolean;
}

interface Fila {
    id: string;
    hora: string;
    nombre: string;
    color: 'green' | 'amber' | 'red' | null;
    linea: string;
}

type Ventana =
    | { tipo: 'admitir'; encargado: string | null }
    | { tipo: 'deshacer' }
    | { tipo: 'evaluar' }
    | { tipo: 'encargado' }
    | { tipo: 'taquilla' }
    | { tipo: 'copia' }
    | null;

const enLinea = () => (typeof navigator === 'undefined' ? true : navigator.onLine);

function horaEn(fecha: Date | string, zona: string | null, segundos = false): string {
    const f = typeof fecha === 'string' ? new Date(fecha) : fecha;
    try {
        return f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', ...(segundos ? { second: '2-digit' } : {}), hourCycle: 'h23', ...(zona === null ? {} : { timeZone: zona }) });
    } catch {
        return f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
}

/** «hoy a las 22:22», «ayer a las 23:05», «mañana hacia las 22:20». */
function diaYHora(fecha: Date, ahora: Date, zona: string | null, hacia = false): string {
    const dia = (d: Date) => {
        try {
            return d.toLocaleDateString('en-CA', zona === null ? {} : { timeZone: zona });
        } catch {
            return d.toISOString().slice(0, 10);
        }
    };
    const diferencia = Math.round((Date.parse(dia(fecha)) - Date.parse(dia(ahora))) / 86_400_000);
    const nombre = diferencia === 0 ? 'hoy' : diferencia === -1 ? 'ayer' : diferencia === 1 ? 'mañana' : fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', ...(zona === null ? {} : { timeZone: zona }) });
    return `${nombre} ${hacia ? 'hacia las' : 'a las'} ${horaEn(fecha, zona)}`;
}

const pila = (n: string | null) => (n ?? '').trim().split(/\s+/)[0] ?? '';

function paseDeLaRespuesta(r: ResultadoValidacion, zona: string | null): PaseVisto | null {
    const p = r.pass;
    if (p == null) {
        return null;
    }
    const d = p.door_display ?? {};
    return {
        id: p.id,
        codigo: p.code,
        nombre: p.holder_name ?? null,
        tipo: d.label ?? null,
        lista: d.list_name ?? null,
        rrpp: d.promoter_name ?? null,
        zona: d.zone ?? null,
        notas: d.notes_for_door ?? null,
        acompanantes: d.companions ?? 0,
        desde: p.valid_from === undefined ? null : horaEn(p.valid_from, zona),
        hasta: p.valid_to === undefined ? null : horaEn(p.valid_to, zona),
    };
}

function paseDeLaCopia(p: ResultadoLocal['pass'], zona: string | null): PaseVisto | null {
    if (p === null) {
        return null;
    }
    const d = p.door_display ?? {};
    const texto = (k: string) => (typeof d[k] === 'string' ? d[k] : null);
    return {
        id: p.pass_id,
        codigo: p.code,
        nombre: p.holder_name ?? null,
        tipo: texto('label'),
        lista: texto('list_name'),
        rrpp: texto('promoter_name'),
        zona: texto('zone'),
        notas: texto('notes_for_door'),
        acompanantes: typeof d.companions === 'number' ? d.companions : 0,
        desde: horaEn(p.valid_from, zona),
        hasta: horaEn(p.valid_to, zona),
    };
}

/** Lo leído: la dirección del QR (cámara o pistola) o un código de 12 caracteres. */
function entradaDe(texto: string): { qr: string } | { codigo: string } | null {
    const limpio = texto.trim();
    if (/v1\.biz_/.test(limpio)) {
        return { qr: limpio };
    }
    const codigo = limpio.replace(/[\s-]+/g, '').toUpperCase();
    return /^[0-9A-HJKMNP-TV-Z]{12}$/.test(codigo) ? { codigo } : null;
}

export function Puerta({ ops, opsTablet, crearOps, negocio, nombreNegocio, nombreTablet, sesion, zona, onCambiarPersona, onTaquilla, ahora = () => new Date(), conexion, almacenCopia }: PropsPuerta) {
    const [navegadorEnLinea, setNavegadorEnLinea] = useState(enLinea);
    const [caida, setCaida] = useState(false);
    const conConexion = (conexion ?? navegadorEnLinea) && !caida;
    const [permisos, setPermisos] = useState<Permisos>(() => permisosDe(null));
    const [grupos, setGrupos] = useState<GrupoDeAcceso[] | null>(null);
    const [elegido, setElegido] = useState<string | null>(null);
    const [detalleGrupo, setDetalleGrupo] = useState<DetalleGrupoDeAcceso | null>(null);
    const [resultado, setResultado] = useState<Resultado | null>(null);
    const [comprobando, setComprobando] = useState(false);
    const [filas, setFilas] = useState<Fila[]>([]);
    const [modo, setModo] = useState<'rapido' | 'detalle'>('rapido');
    const [camara, setCamara] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [encontrados, setEncontrados] = useState<{ id: string; nombre: string; linea: string; codigo: string }[] | null>(null);
    const [ventana, setVentana] = useState<Ventana>(null);
    const [error, setError] = useState<string | null>(null);
    const copia = useCopia({ ops, opsTablet, negocio, conConexion, ...(almacenCopia === undefined ? {} : { almacen: almacenCopia }) });

    useEffect(() => {
        const si = () => { setNavegadorEnLinea(true); setCaida(false); };
        const no = () => { setNavegadorEnLinea(false); };
        window.addEventListener('online', si);
        window.addEventListener('offline', no);
        return () => {
            window.removeEventListener('online', si);
            window.removeEventListener('offline', no);
        };
    }, []);

    // Permisos de la persona (getMyNavigation): solo para enseñar u ocultar botones; la API decide igualmente.
    useEffect(() => {
        let vivo = true;
        ops.getMyNavigation(negocio)
            .then((n) => {
                if (vivo) {
                    setPermisos(permisosDe(n));
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    // El evento o la jornada de ahora (G782): los que se cruzan con estas 24 h.
    useEffect(() => {
        let vivo = true;
        const t = ahora().getTime();
        ops.listAccessGroups(negocio, { status: 'active', from: new Date(t - 12 * 3_600_000).toISOString(), to: new Date(t + 12 * 3_600_000).toISOString() }, { limit: 20 })
            .then((l) => {
                if (vivo) {
                    setGrupos(l.data);
                }
            })
            .catch(() => {
                if (vivo) {
                    setGrupos([]);
                }
            });
        return () => {
            vivo = false;
        };
        // ahora solo se mira al abrir.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio]);

    const grupo = grupos?.find((g) => g.id === (elegido ?? copia.estado.grupo)) ?? grupos?.[0] ?? null;
    const idGrupo = grupo?.id ?? null;

    const leerGrupo = useCallback(() => {
        if (idGrupo === null || !conConexion) {
            return;
        }
        ops.getAccessGroup(negocio, idGrupo).then(setDetalleGrupo).catch(() => undefined);
    }, [ops, negocio, idGrupo, conConexion]);
    useEffect(() => {
        leerGrupo();
        const reloj = setInterval(leerGrupo, 30_000);
        return () => {
            clearInterval(reloj);
        };
    }, [leerGrupo]);

    const datos = useCallback(
        (p: PaseVisto | null, previa: ResultadoValidacion['previous_arrival'] = null): DatosLectura => ({
            nombreNegocio,
            tipo: p?.tipo,
            lista: p?.lista,
            zona: p?.zona,
            desde: p?.desde,
            hasta: p?.hasta,
            antes: previa == null ? null : { hora: horaEn(previa.arrived_at, zona), tablet: previa.by?.device_name ?? 'otra tablet', quien: pila(previa.by?.member_name ?? '') },
            ahora: horaEn(ahora(), zona),
            copia: copia.estado.ultimaRespuesta === null ? null : horaEn(copia.estado.ultimaRespuesta, zona),
        }),
        [nombreNegocio, zona, ahora, copia.estado.ultimaRespuesta],
    );

    const anadirFila = useCallback(
        (r: Resultado, previa: ResultadoValidacion['previous_arrival'] = null) => {
            const linea = r.motivo === 'not_own' ? NO_ES_TUYA.titulo : lineaCorta(r.motivo, datos(r.pase, previa));
            setFilas((f) => [{ id: crypto.randomUUID(), hora: horaEn(ahora(), zona), nombre: r.pase?.nombre ?? (r.pase === null ? 'Pase desconocido' : 'Pase'), color: r.color, linea }, ...f].slice(0, 20));
        },
        [ahora, zona, datos],
    );

    /** Sin conexión, un ámbar que se deja sin decidir se anota tal cual al leer el siguiente. */
    const anotarPendiente = useCallback(
        (r: Resultado | null, decision: { decision: 'admitted' | 'rejected'; motivo?: string } | null) => {
            if (r?.local === undefined || r.decidido === true) {
                return;
            }
            copia.anotar({ item: elementoDeLectura(r.local.r, r.local.entrada, sesion.memberId, r.local.cuando, crypto.randomUUID(), decision), nombre: r.pase?.nombre ?? null });
        },
        [copia, sesion.memberId],
    );

    const aviso = copia.estado.ultimaRespuesta === null ? null : avisoSinConexion(copia.estado.ultimaRespuesta, ahora());
    const caducada = !conConexion && aviso?.estado === 'caducada';

    const validarConCopia = useCallback(
        async (entrada: { qr: string } | { codigo: string } | { pase: string }) => {
            const c: Copia | null = copia.estado.copia;
            if (c === null) {
                setError(T_PUERTA.sinConexionSinCopia);
                return;
            }
            const cuando = ahora();
            const pase = 'pase' in entrada ? c.passes.find((p) => p.pass_id === entrada.pase) : undefined;
            const real = 'pase' in entrada ? { codigo: pase?.code ?? '' } : entrada;
            const yaLeidos = new Set(copia.estado.pendientes.map((p) => p.item).filter(esLectura).filter((i) => dejaDentro(i) && i.code !== undefined).map((i) => i.code ?? ''));
            const r = await validarSinConexion(c, real, cuando, yaLeidos);
            const visto = paseDeLaCopia(r.pass, zona);
            const { titulo, texto } = explicar(r.reason, datos(visto));
            const nuevo: Resultado = { color: r.result, motivo: r.reason, titulo, texto, pase: visto, lectura: null, admisible: r.admissible, sinConexion: true, local: { r, entrada: real, cuando } };
            if (r.result !== 'amber') {
                copia.anotar({ item: elementoDeLectura(r, real, sesion.memberId, cuando), nombre: visto?.nombre ?? null });
                nuevo.decidido = true;
            }
            setResultado(nuevo);
            anadirFila(nuevo);
        },
        [copia, ahora, zona, datos, sesion.memberId, anadirFila],
    );

    const validar = useCallback(
        (entrada: { qr: string } | { codigo: string } | { pase: string }) => {
            if (comprobando || caducada) {
                return;
            }
            anotarPendiente(resultado, null);
            setError(null);
            setEncontrados(null);
            setBusqueda('');
            if (!conConexion) {
                void validarConCopia(entrada);
                return;
            }
            setComprobando(true);
            const cuerpo = 'qr' in entrada
                ? { method: 'qr' as const, qr_payload: entrada.qr.slice(0, 300) }
                : 'codigo' in entrada
                  ? { method: 'typed_code' as const, code: entrada.codigo }
                  : { method: 'name_search' as const, pass_id: entrada.pase };
            ops.validatePass(negocio, { ...cuerpo, ...(idGrupo === null ? {} : { access_group_id: idGrupo }), client_scan_id: crypto.randomUUID(), device_scanned_at: ahora().toISOString() })
                .then((r) => {
                    const visto = paseDeLaRespuesta(r, zona);
                    const { titulo, texto } = explicar(r.reason, datos(visto, r.previous_arrival));
                    const nuevo: Resultado = { color: r.result, motivo: r.reason, titulo, texto, pase: visto, lectura: r.scan.id, admisible: r.admissible, sinConexion: false };
                    setResultado(nuevo);
                    anadirFila(nuevo, r.previous_arrival);
                    leerGrupo();
                })
                .catch((e: unknown) => {
                    if (e instanceof ErrorApi && e.estado === 403 && permisos.ambito('pases.puerta.validar') === 'own') {
                        // G415: el profesional solo registra sus citas.
                        const nuevo: Resultado = { color: 'red', motivo: 'not_own', titulo: NO_ES_TUYA.titulo, texto: NO_ES_TUYA.texto, pase: null, lectura: null, admisible: false, sinConexion: false };
                        setResultado(nuevo);
                        anadirFila(nuevo);
                    } else if (e instanceof ErrorApi) {
                        setError(e.problema.detail ?? e.problema.title);
                    } else {
                        // Sin red: se sigue con la copia.
                        setCaida(true);
                        void validarConCopia(entrada);
                    }
                })
                .finally(() => {
                    setComprobando(false);
                });
        },
        [comprobando, caducada, anotarPendiente, resultado, conConexion, validarConCopia, ops, negocio, idGrupo, ahora, zona, datos, anadirFila, leerGrupo, permisos],
    );

    // El lector de pistola teclea la dirección del QR y Enter, fuera de cualquier campo.
    const tecleado = useRef('');
    const validarRef = useRef(validar);
    useEffect(() => {
        validarRef.current = validar;
    }, [validar]);
    useEffect(() => {
        const tecla = (e: KeyboardEvent) => {
            const destino = e.target as HTMLElement | null;
            if (destino !== null && (destino.tagName === 'INPUT' || destino.tagName === 'TEXTAREA' || destino.tagName === 'SELECT')) {
                return;
            }
            if (e.key === 'Enter') {
                const entrada = entradaDe(tecleado.current);
                tecleado.current = '';
                if (entrada !== null) {
                    validarRef.current(entrada);
                }
            } else if (e.key.length === 1) {
                tecleado.current = (tecleado.current + e.key).slice(-300);
            }
        };
        window.addEventListener('keydown', tecla);
        return () => {
            window.removeEventListener('keydown', tecla);
        };
    }, []);

    const buscar = () => {
        const q = busqueda.trim();
        const entrada = entradaDe(q);
        if (entrada !== null) {
            validar(entrada);
            return;
        }
        if (q.length < 2) {
            return;
        }
        if (!conConexion) {
            const c = copia.estado.copia;
            const t = q.toLocaleLowerCase('es');
            setEncontrados((c?.passes ?? []).filter((p) => (p.holder_name ?? '').toLocaleLowerCase('es').includes(t) && (p.status === 'active' || p.status === 'inside')).slice(0, 10).map((p) => ({ id: p.pass_id, nombre: p.holder_name ?? p.code, linea: typeof p.door_display?.label === 'string' ? p.door_display.label : '', codigo: p.code })));
            return;
        }
        ops.listPasses(negocio, { q, ...(idGrupo === null ? {} : { access_group_id: idGrupo }), limit: 10 })
            .then((l) => {
                setEncontrados(l.data.filter((p) => p.status === 'active' || p.status === 'inside').map((p) => ({ id: p.id, nombre: p.holder_name ?? p.code, linea: p.door_display?.label ?? '', codigo: p.code })));
            })
            .catch(() => {
                setEncontrados([]);
            });
    };

    const decidir = (decision: 'admit' | 'reject', motivo: string | null, conOps: Operaciones = ops) => {
        const r = resultado;
        if (r === null) {
            return;
        }
        if (r.sinConexion) {
            anotarPendiente(r, { decision: decision === 'admit' ? 'admitted' : 'rejected', ...(motivo === null ? {} : { motivo }) });
            const admitido = decision === 'admit';
            const nuevo: Resultado = { ...r, decidido: true, color: admitido ? 'green' : 'red', motivo: admitido ? 'admitted_override' : r.motivo, ...(admitido ? explicar('admitted_override', datos(r.pase)) : {}), admisible: false };
            setResultado(nuevo);
            anadirFila(nuevo);
            setVentana(null);
            return;
        }
        if (r.lectura === null) {
            return;
        }
        conOps.decideAmberScan(negocio, r.lectura, { decision, ...(motivo === null ? {} : { reason: motivo }) })
            .then((res) => {
                const visto = paseDeLaRespuesta(res, zona) ?? r.pase;
                const { titulo, texto } = explicar(res.reason, datos(visto, res.previous_arrival));
                const nuevo: Resultado = { color: res.result, motivo: res.reason, titulo, texto, pase: visto, lectura: res.scan.id, admisible: false, sinConexion: false, decidido: true };
                setResultado(decision === 'reject' ? null : nuevo);
                if (decision === 'admit') {
                    anadirFila(nuevo);
                }
                setVentana(null);
                leerGrupo();
            })
            .catch((e: unknown) => {
                setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido guardar la decisión. Prueba de nuevo.');
                setVentana(null);
            });
    };

    const deshacer = (motivo: string) => {
        const p = resultado?.pase;
        if (p?.id == null) {
            return;
        }
        ops.undoArrival(negocio, p.id, motivo)
            .then(() => {
                setFilas((f) => [{ id: crypto.randomUUID(), hora: horaEn(ahora(), zona), nombre: p.nombre ?? 'Pase', color: null, linea: T_PUERTA.deshecha(motivo) }, ...f].slice(0, 20));
                setResultado(null);
                setVentana(null);
                leerGrupo();
            })
            .catch((e: unknown) => {
                setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido deshacer la llegada.');
                setVentana(null);
            });
    };

    const puedeAdmitir = permisos.puede('pases.puerta.admitir_ambar');
    const puedeDeshacer = permisos.puede('pases.llegadas.deshacer');
    const puedeEvaluar = permisos.puede('clientes.evaluaciones.registrar') && permisos.puede('pases.pases.ver');
    const profesional = permisos.ambito('pases.puerta.validar') === 'own';
    /** La evaluación recién guardada en esta tablet (la fila de la ficha la enseña en vez de la vigente). */
    const [evaluacionGuardada, setEvaluacionGuardada] = useState<{ pase: string; evaluacion: EvaluacionGuardada } | null>(null);
    const [vueltaEvaluacion, setVueltaEvaluacion] = useState(0);
    const caja = copia.estado.copia?.box_office ?? null;
    // Lo vendido en la taquilla sin conexión y aún sin enviar ya no queda (G509).
    const queda = caja === null ? null : cupoSinConexion(caja, copia.estado.pendientes);
    const inside = detalleGrupo?.attendance.inside ?? null;
    const emitidos = detalleGrupo?.attendance.issued ?? null;
    // Las llegadas por enviar (las ventas de la taquilla sin conexión van en el mismo lote, pero no son llegadas).
    const pendientes = copia.estado.pendientes.filter((p) => esLectura(p.item)).length;

    // La franja de arriba: sin copia, sin conexión (con su tope) o el resultado de enviar lo leído.
    const franja = useMemo(() => {
        if (copia.estado.enviando) {
            return { clase: 'tinta', icono: 'loader-circle' as const, titulo: T_PUERTA.enviando, texto: '' };
        }
        // G985: si este lote lo envió la Puerta y alguna venta llegó sin su contacto, el aviso «Entradas sin enviar» sale aquí.
        if (copia.estado.resultado !== null && copia.estado.resultado.sinContacto > 0) {
            return { clase: 'ambar', icono: 'triangle-alert' as const, titulo: TITULO_SIN_CONTACTO, texto: textoSinContacto(copia.estado.resultado.sinContacto), entendido: true };
        }
        if (conConexion && copia.estado.resultado !== null) {
            const r = copia.estado.resultado;
            return { clase: 'tinta', icono: 'check' as const, titulo: T_PUERTA.enviadas(r.enviadas), texto: [r.rechazadas > 0 ? T_PUERTA.rechazadas(r.rechazadas) : '', r.conflictos > 0 ? T_PUERTA.conflictos(r.conflictos) : ''].filter((x) => x !== '').join(' ') };
        }
        if (!conConexion && !copia.estado.hayCopia) {
            return { clase: 'rojo', icono: 'wifi-off' as const, titulo: T_PUERTA.sinConexionSinCopia, texto: T_PUERTA.sinConexionSinCopiaTexto };
        }
        if (!conConexion && aviso !== null && copia.estado.ultimaRespuesta !== null) {
            const ultima = diaYHora(copia.estado.ultimaRespuesta, ahora(), zona);
            const hasta = diaYHora(aviso.hasta, ahora(), zona, true);
            if (aviso.estado === 'caducada') {
                return { clase: 'rojo', icono: 'wifi-off' as const, titulo: TEXTOS_SIN_CONEXION.caducada, texto: T_PUERTA.ultimaConexion(ultima, hasta) };
            }
            return {
                clase: aviso.estado === 'conectate' ? 'rojo' : 'ambar',
                icono: 'wifi-off' as const,
                titulo: aviso.estado === 'conectate' ? TEXTOS_SIN_CONEXION.conectate : T_PUERTA.sinConexionCopia(horaEn(copia.estado.ultimaRespuesta, zona)),
                texto: textoFranjaSinConexion(aviso.estado, T_PUERTA.ultimaConexion(ultima, hasta), pendientes > 0 ? T_PUERTA.porEnviar(pendientes) : null),
            };
        }
        if (conConexion && !copia.estado.hayCopia && idGrupo !== null && !profesional) {
            return { clase: 'suave', icono: 'download' as const, titulo: T_PUERTA.sinCopia, texto: T_PUERTA.sinCopiaTexto, boton: true };
        }
        return null;
    }, [copia.estado, conConexion, aviso, ahora, zona, pendientes, idGrupo, profesional]);

    const r = resultado;
    const color = r === null ? null : COLOR[r.color];

    return (
        <div className="rv-pt">
            <header className="rv-pt__cab">
                <span className="rv-pt__donde">
                    <small>{`${nombreNegocio} · ${nombreTablet}`}</small>
                    {grupos !== null && grupos.length > 1 ? (
                        <select aria-label={T_PUERTA.eventoOJornada} value={idGrupo ?? ''} onChange={(e) => { setElegido(e.target.value); setDetalleGrupo(null); }}>
                            {grupos.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                        </select>
                    ) : (
                        <strong>{grupo === null ? (grupos === null ? '' : T_PUERTA.sinGrupo) : `${grupo.label} · ${horaEn(grupo.valid_from, zona)}–${horaEn(grupo.valid_to, zona)}`}</strong>
                    )}
                </span>
                <button type="button" className={`rv-pt__conexion${conConexion ? '' : ' rv-pt__conexion--no'}`} onClick={() => { setVentana({ tipo: 'copia' }); }}>
                    <Icono nombre={conConexion ? 'wifi' : 'wifi-off'} />
                    {conConexion ? T_PUERTA.conConexion : T_PUERTA.sinConexion}
                </button>
                <span className="rv-tablet__avatar rv-pt__yo" title={sesion.nombre}>{sesion.iniciales}</span>
                <button type="button" className="rv-pt__cambiar" onClick={onCambiarPersona}>
                    <Icono nombre="users" />
                    {T_PUERTA.cambiar}
                </button>
            </header>

            {franja !== null && (
                <div className={`rv-pt__franja rv-pt__franja--${franja.clase}`} role="status">
                    <Icono nombre={franja.icono} />
                    <span><strong>{franja.titulo}</strong>{franja.texto !== '' && <span>{franja.texto}</span>}</span>
                    {'entendido' in franja && (
                        <button type="button" className="rv-pt__b rv-pt__b--claro" onClick={copia.olvidarResultado}>
                            {T_PUERTA.entendido}
                        </button>
                    )}
                    {'boton' in franja && grupo !== null && (
                        <button type="button" className="rv-pt__b rv-pt__b--claro" disabled={copia.estado.ocupada} onClick={() => { setVentana({ tipo: 'copia' }); }}>
                            <Icono nombre="download" />
                            {T_PUERTA.descargar}
                        </button>
                    )}
                </div>
            )}

            <div className="rv-pt__cuerpo">
                <main className={`rv-pt__visor${color === null ? '' : ` rv-pt__visor--${color.clase}`}`}>
                    <Camara abierta={camara && !caducada} onLeido={(v) => { const e = entradaDe(v); if (e !== null) validar(e); }} />
                    {comprobando && (
                        <div className="rv-pt__comprobando" role="status"><Icono nombre="loader-circle" className="rv-gira" />{T_PUERTA.comprobando}<small>{conConexion ? T_PUERTA.validando : T_PUERTA.conLaCopia}</small></div>
                    )}
                    {error !== null && <p className="rv-pt__error" role="alert"><Icono nombre="circle-alert" />{error}</p>}
                    {r !== null && color !== null && (
                        <section className={`rv-pt-res rv-pt-res--${color.clase}`} aria-live="assertive" aria-label={color.palabra}>
                            <div className="rv-pt-res__cab">
                                <span className="rv-pt-res__icono"><Icono nombre={color.icono} /></span>
                                <strong className="rv-pt-res__palabra">{color.palabra}</strong>
                            </div>
                            {r.pase?.nombre != null && <strong className="rv-pt-res__nombre">{r.pase.nombre}</strong>}
                            <span className="rv-pt-res__titulo">{r.titulo}</span>
                            {r.texto !== '' && <span className="rv-pt-res__texto">{r.texto}</span>}
                            {r.pase !== null && r.pase.acompanantes > 0 && <span className="rv-pt-res__texto"><Icono nombre="users" /> {T_PUERTA.acompanantes(r.pase.acompanantes)}</span>}
                            {r.pase?.notas != null && <span className="rv-pt-res__nota"><Icono nombre="sticky-note" /> {r.pase.notas}</span>}
                            {modo === 'detalle' && r.pase !== null && (
                                <dl className="rv-pt-res__ficha">
                                    {([
                                        ['Tipo', r.pase.tipo],
                                        ['Lista', r.pase.lista],
                                        ['RRPP', r.pase.rrpp],
                                        ['Zona', r.pase.zona],
                                        ['Horario', r.pase.desde === null ? null : `De ${r.pase.desde} a ${r.pase.hasta ?? ''}`],
                                        ['Código', r.pase.codigo],
                                    ] as [string, string | null][]).filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
                                        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                                    ))}
                                </dl>
                            )}
                            <div className="rv-pt-res__acciones">
                                {r.color === 'amber' && r.decidido !== true && (
                                    <>
                                        <button type="button" className="rv-pt__b rv-pt__b--suave" onClick={() => { decidir('reject', null); }}><Icono nombre="x" />{T_PUERTA.rechazar}</button>
                                        {r.admisible && puedeAdmitir && (
                                            <button type="button" className="rv-pt__b rv-pt__b--tinta" onClick={() => { setVentana({ tipo: 'admitir', encargado: null }); }}><Icono nombre="check" />{T_PUERTA.admitir}</button>
                                        )}
                                    </>
                                )}
                                {modo === 'detalle' && r.color === 'green' && r.pase?.id != null && puedeDeshacer && (
                                    <button type="button" className="rv-pt__b rv-pt__b--suave" disabled={!conConexion} title={conConexion ? undefined : T_PUERTA.deshacerSinConexion} onClick={() => { setVentana({ tipo: 'deshacer' }); }}><Icono nombre="undo-2" />{T_PUERTA.deshacer}</button>
                                )}
                            </div>
                            {modo === 'detalle' && r.pase?.id != null && puedeEvaluar && !profesional && (
                                <FilaEvaluacion
                                    key={`${r.pase.id}·${String(vueltaEvaluacion)}`}
                                    ops={ops}
                                    negocio={negocio}
                                    paseId={r.pase.id}
                                    zona={zona}
                                    conConexion={conConexion}
                                    guardada={evaluacionGuardada?.pase === r.pase.id ? evaluacionGuardada.evaluacion : null}
                                    onEvaluar={() => { setVentana({ tipo: 'evaluar' }); }}
                                />
                            )}
                            {r.color === 'amber' && r.decidido !== true && r.admisible && !puedeAdmitir && (
                                <div className="rv-pt-res__encargado">
                                    <strong>{T_PUERTA.soloEncargado}</strong>
                                    <button type="button" className="rv-pt__b rv-pt__b--tinta" onClick={() => { setVentana({ tipo: 'encargado' }); }}><Icono nombre="key-round" />{T_PUERTA.pedirEncargado}</button>
                                </div>
                            )}
                            {modo === 'detalle' && !conConexion && r.color === 'green' && puedeDeshacer && <small>{T_PUERTA.deshacerSinConexion}</small>}
                        </section>
                    )}
                </main>

                <aside className="rv-pt__lado">
                    {grupo !== null && (
                        <section className="rv-pt__dentro" aria-label={T_PUERTA.dentro}>
                            <span>{T_PUERTA.dentro}{conConexion ? '' : ' · aprox.'}</span>
                            <strong>{inside === null ? '—' : String(inside)}</strong>
                            {emitidos !== null && <small>{`de ${String(emitidos)} pases`}</small>}
                            {!conConexion && pendientes > 0 && <small>{`${String(pendientes)} sin enviar`}</small>}
                        </section>
                    )}
                    <form className="rv-pt__buscar" role="search" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
                        <Icono nombre="search" />
                        <input type="search" aria-label={profesional ? T_PUERTA.buscarProfesional : T_PUERTA.buscar} placeholder={profesional ? T_PUERTA.buscarProfesional : T_PUERTA.buscar} value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setEncontrados(null); }} />
                    </form>
                    {encontrados !== null && (
                        <ul className="rv-pt__lista" aria-label="Resultados de la búsqueda">
                            {encontrados.length === 0 ? <li className="rv-pt__vacio">{T_PUERTA.sinResultados}</li> : encontrados.map((p) => (
                                <li key={p.id}>
                                    <button type="button" onClick={() => { validar({ pase: p.id }); }}>
                                        <strong>{p.nombre}</strong>
                                        <small>{[p.linea, p.codigo].filter((x) => x !== '').join(' · ')}</small>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {!profesional && (
                        <section className="rv-pt__ultimas" aria-label={T_PUERTA.ultimas}>
                            <header><strong>{T_PUERTA.ultimas}</strong><small>{nombreTablet}</small></header>
                            <ul>
                                {filas.map((f) => (
                                    <li key={f.id} className={f.color === null ? 'rv-pt__fila--deshecha' : `rv-pt__fila--${COLOR[f.color].clase}`}>
                                        <span className="rv-pt__hora">{f.hora}</span>
                                        <span className="rv-pt__marca"><Icono nombre={f.color === null ? 'undo-2' : COLOR[f.color].icono} /></span>
                                        <span><strong>{f.nombre}</strong><small>{f.linea}</small></span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                    <nav className="rv-pt__controles">
                        <button type="button" aria-pressed={modo === 'rapido'} onClick={() => { setModo('rapido'); }}><Icono nombre="zap" />{T_PUERTA.rapido}</button>
                        <button type="button" aria-pressed={modo === 'detalle'} onClick={() => { setModo('detalle'); }}><Icono nombre="file-text" />{T_PUERTA.detalle}</button>
                        {!profesional && grupo !== null && (
                            <button type="button" aria-label={queda === null || caja === null ? T_PUERTA.taquilla : `${T_PUERTA.taquilla}: quedan ${String(queda)} de ${String(caja.quota)} en esta tablet`} onClick={() => { setVentana({ tipo: 'taquilla' }); }}>
                                <Icono nombre="banknote" />
                                {T_PUERTA.taquilla}
                                {queda !== null && <span className={`rv-pt__insignia${queda === 0 ? ' rv-pt__insignia--rojo' : ''}`}>{String(queda)}</span>}
                            </button>
                        )}
                        <button type="button" aria-pressed={!camara} onClick={() => { setCamara((c) => !c); }}><Icono nombre={camara ? 'camera-off' : 'camera'} />{camara ? T_PUERTA.cerrar : T_PUERTA.abrirCamara}</button>
                    </nav>
                </aside>
            </div>

            {ventana?.tipo === 'admitir' && r !== null && (
                <VentanaMotivo
                    titulo={T_PUERTA.admitirA(pila(r.pase?.nombre ?? '') || 'esta persona')}
                    sub={T_PUERTA.admitirSub(r.titulo)}
                    presets={PRESETS_ADMITIR}
                    boton={T_PUERTA.admitirBoton}
                    onCancelar={() => { setVentana(null); }}
                    onAceptar={(m) => { decidir('admit', m, ventana.encargado === null ? ops : crearOps(() => ventana.encargado)); }}
                />
            )}
            {ventana?.tipo === 'deshacer' && r !== null && (
                <VentanaMotivo titulo={T_PUERTA.deshacerDe(pila(r.pase?.nombre ?? '') || 'esta persona')} sub="Queda en Auditoría con tu nombre y el motivo." presets={[]} boton={T_PUERTA.deshacer} onCancelar={() => { setVentana(null); }} onAceptar={deshacer} />
            )}
            {ventana?.tipo === 'encargado' && (
                <PinEncargado
                    opsTablet={opsTablet}
                    yo={sesion.memberId}
                    onCancelar={() => { setVentana(null); }}
                    onListo={(token) => { setVentana({ tipo: 'admitir', encargado: token }); }}
                />
            )}
            {ventana?.tipo === 'evaluar' && r?.pase?.id != null && (
                <Evaluar
                    ops={ops}
                    negocio={negocio}
                    paseId={r.pase.id}
                    nombre={r.pase.nombre}
                    zona={zona}
                    onGuardada={(evaluacion) => { if (r.pase?.id != null) setEvaluacionGuardada({ pase: r.pase.id, evaluacion }); }}
                    onCerrar={() => { setVentana(null); setVueltaEvaluacion((n) => n + 1); }}
                />
            )}
            {ventana?.tipo === 'taquilla' && grupo !== null && (
                <Hoja titulo={T_PUERTA.taquillaTitulo} onCerrar={() => { setVentana(null); }}>
                    {caja === null ? (
                        <p>{T_PUERTA.sinCopiaTexto}</p>
                    ) : (
                        <dl className="rv-pt__cifras">
                            <div><dt>{T_PUERTA.cupo}</dt><dd>{caja.quota}</dd></div>
                            <div><dt>{T_PUERTA.vendidas}</dt><dd>{caja.used}</dd></div>
                            <div><dt>{T_PUERTA.quedan}</dt><dd>{queda}</dd></div>
                        </dl>
                    )}
                    {!conConexion && <p>{T_PUERTA.taquillaSinConexion}</p>}
                    {queda === 0 && <p>{T_PUERTA.agotada}</p>}
                    <button type="button" className="rv-pt__b rv-pt__b--rojo" disabled={queda === 0} onClick={() => { onTaquilla(grupo); }}><Icono nombre="banknote" />{T_PUERTA.abrirTaquilla}</button>
                </Hoja>
            )}
            {ventana?.tipo === 'copia' && (
                <VentanaCopia
                    conConexion={conConexion}
                    hayCopia={copia.estado.hayCopia}
                    ultima={copia.estado.ultimaRespuesta === null ? '—' : diaYHora(copia.estado.ultimaRespuesta, ahora(), zona)}
                    hasta={aviso === null ? null : diaYHora(aviso.hasta, ahora(), zona, true)}
                    pendientes={pendientes}
                    ocupada={copia.estado.ocupada}
                    error={copia.estado.error}
                    puedeDescargar={grupo !== null && conConexion}
                    onDescargar={(cupo) => { if (grupo !== null) void copia.descargar(grupo.id, cupo).then(() => { setVentana(null); }); }}
                    onCerrar={() => { setVentana(null); }}
                />
            )}
        </div>
    );
}

function Hoja({ titulo, sub, onCerrar, children }: { titulo: string; sub?: string; onCerrar: () => void; children: ReactNode }) {
    return (
        <div className="rv-pt-capa" role="presentation" onClick={onCerrar}>
            <div className="rv-pt-hoja" role="dialog" aria-modal="true" aria-label={titulo} onClick={(e) => { e.stopPropagation(); }}>
                <h2>{titulo}</h2>
                {sub !== undefined && <p>{sub}</p>}
                {children}
            </div>
        </div>
    );
}

function VentanaMotivo({ titulo, sub, presets, boton, onCancelar, onAceptar }: { titulo: string; sub: string; presets: readonly string[]; boton: string; onCancelar: () => void; onAceptar: (motivo: string) => void }) {
    const [motivo, setMotivo] = useState('');
    return (
        <Hoja titulo={titulo} sub={sub} onCerrar={onCancelar}>
            {presets.length > 0 && (
                <div className="rv-pt-hoja__presets">
                    {presets.map((p) => <button key={p} type="button" aria-pressed={motivo === p} onClick={() => { setMotivo(p); }}>{p}</button>)}
                </div>
            )}
            <label className="rv-pt-hoja__campo">
                <span>{T_PUERTA.motivo}</span>
                <textarea value={motivo} maxLength={500} onChange={(e) => { setMotivo(e.target.value); }} />
            </label>
            <small>{T_PUERTA.quedaAuditoria}</small>
            <div className="rv-pt-hoja__botones">
                <button type="button" className="rv-pt__b rv-pt__b--claro" onClick={onCancelar}><Icono nombre="x" />{T_PUERTA.cancelar}</button>
                <button type="button" className="rv-pt__b rv-pt__b--rojo" disabled={motivo.trim() === ''} onClick={() => { onAceptar(motivo.trim()); }}><Icono nombre="check" />{boton}</button>
            </div>
        </Hoja>
    );
}

/** G783: un encargado admite el ámbar con su PIN en esta tablet, solo para esta decisión. */
function PinEncargado({ opsTablet, yo, onCancelar, onListo }: { opsTablet: Operaciones; yo: string; onCancelar: () => void; onListo: (token: string) => void }) {
    const [personas, setPersonas] = useState<PersonaDeLaTablet[] | null>(null);
    const [persona, setPersona] = useState<PersonaDeLaTablet | null>(null);
    const [pin, setPin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    useEffect(() => {
        opsTablet.listDeviceMembers()
            .then((r) => { setPersonas(r.data.filter((p) => p.member_id !== yo)); })
            .catch(() => { setPersonas([]); });
    }, [opsTablet, yo]);
    const entrar = () => {
        if (persona === null) {
            return;
        }
        setOcupado(true);
        setError(null);
        opsTablet.startMemberSessionOnDevice(persona.member_id, pin)
            .then((t) => { onListo(t.access_token); })
            .catch(() => {
                setPin('');
                setError('El PIN no es correcto.');
            })
            .finally(() => { setOcupado(false); });
    };
    return (
        <Hoja titulo={T_PUERTA.pinEncargado} sub={T_PUERTA.pinEncargadoSub} onCerrar={onCancelar}>
            {persona === null ? (
                <div className="rv-pt-hoja__personas">
                    {(personas ?? []).filter((p) => !p.pin_locked).map((p) => (
                        <button key={p.member_id} type="button" onClick={() => { setPersona(p); setPin(''); }}>
                            <span className="rv-tablet__avatar">{p.initials}</span>
                            <span>{p.display_name}</span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="rv-pt-hoja__pin">
                    <strong>{persona.display_name}</strong>
                    <div className="rv-tablet__puntos" aria-label={`${String(pin.length)} cifras escritas`}>
                        {Array.from({ length: Math.max(4, pin.length) }, (_, i) => <span key={i} className={i < pin.length ? 'rv-tablet__punto--lleno' : undefined} />)}
                    </div>
                    {error !== null && <p role="alert" className="rv-tablet__error"><Icono nombre="circle-alert" />{error}</p>}
                    <Teclado
                        onDigito={(d) => { if (!ocupado && pin.length < 6) setPin(pin + d); }}
                        onBorrar={() => { setPin((p) => p.slice(0, -1)); }}
                        confirmar={{ etiqueta: 'Entrar', icono: 'check', activo: pin.length >= 4 && !ocupado, accion: entrar }}
                    />
                </div>
            )}
            <div className="rv-pt-hoja__botones">
                <button type="button" className="rv-pt__b rv-pt__b--claro" onClick={onCancelar}><Icono nombre="x" />{T_PUERTA.cancelar}</button>
            </div>
        </Hoja>
    );
}

interface EvaluacionGuardada {
    nivel: 'green' | 'amber' | 'red';
    motivo: MotivoEvaluacion | null;
    /** `null` en verde: quita la vigente (204). */
    caduca: string | null;
}

const PALABRA_NIVEL: Record<'green' | 'amber' | 'red', string> = { green: 'Verde', amber: 'Ámbar', red: 'Rojo' };
const palabraMotivo = (m: MotivoEvaluacion | null | undefined): string | null => MOTIVOS_EVALUACION.find(([k]) => k === m)?.[1] ?? null;

/** «sáb 13 mar 2027», en la zona del negocio. */
function fechaEvaluacion(iso: string, zona: string | null): string {
    const partes = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', ...(zona === null ? {} : { timeZone: zona }) }).formatToParts(new Date(iso));
    const parte = (t: Intl.DateTimeFormatPartTypes) => (partes.find((x) => x.type === t)?.value ?? '').replace('.', '');
    return `${parte('weekday')} ${parte('day')} ${parte('month')} ${parte('year')}`;
}

/**
 * La fila «Evaluación de puerta» de la ficha del pase (6.1, G193, revisión del 31-oct): la vigente del titular
 * (`getPass` → `customer_id` → `listDoorAssessments` con `limit=1`) y «Evaluar» o «Cambiar»; sin conexión, apagado.
 */
function FilaEvaluacion({ ops, negocio, paseId, zona, conConexion, guardada, onEvaluar }: { ops: Operaciones; negocio: string; paseId: string; zona: string | null; conConexion: boolean; guardada: EvaluacionGuardada | null; onEvaluar: () => void }) {
    const [vigente, setVigente] = useState<EvaluacionPuerta | null>(null);
    useEffect(() => {
        if (!conConexion) {
            return undefined;
        }
        let vivo = true;
        ops.getPass(negocio, paseId)
            .then((p) => (p.customer_id == null ? null : ops.listDoorAssessments(negocio, p.customer_id, { limit: 1 })))
            .then((l) => { if (vivo) setVigente(l?.data[0] ?? null); })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, paseId, conConexion]);
    const texto = !conConexion
        ? T_PUERTA.evaluacionSinConexion
        : guardada !== null
          ? T_PUERTA.evaluacionGuardada(PALABRA_NIVEL[guardada.nivel], palabraMotivo(guardada.motivo), guardada.caduca === null ? null : fechaEvaluacion(guardada.caduca, zona))
          : vigente !== null
            ? T_PUERTA.evaluacionVigente(PALABRA_NIVEL[vigente.level], palabraMotivo(vigente.reason_code), fechaEvaluacion(vigente.expires_at, zona))
            : T_PUERTA.evaluacionSinVigente;
    const hay = guardada !== null || vigente !== null;
    return (
        <div className="rv-pt-res__evaluacion">
            <span>
                <strong>{T_PUERTA.evaluacion}</strong>
                <small>{texto}</small>
            </span>
            <button type="button" className="rv-pt__b rv-pt__b--suave" disabled={!conConexion} onClick={onEvaluar}><Icono nombre="gauge" />{hay ? T_PUERTA.cambiarEvaluacion : T_PUERTA.evaluar}</button>
        </div>
    );
}

/** Evaluación de puerta (G193): `getPass` da el `customer_id`; la vigente con `listDoorAssessments`; guardar con `pass_ref`. */
function Evaluar({ ops, negocio, paseId, nombre, zona, onGuardada, onCerrar }: { ops: Operaciones; negocio: string; paseId: string; nombre: string | null; zona: string | null; onGuardada?: (e: EvaluacionGuardada) => void; onCerrar: () => void }) {
    const [cliente, setCliente] = useState<string | null | undefined>(undefined);
    const [vigente, setVigente] = useState<EvaluacionPuerta | null>(null);
    const [nivel, setNivel] = useState<'green' | 'amber' | 'red' | null>(null);
    const [motivo, setMotivo] = useState<MotivoEvaluacion | null>(null);
    const [nota, setNota] = useState('');
    const [estado, setEstado] = useState<'normal' | 'guardando' | 'hecho' | 'error'>('normal');
    useEffect(() => {
        let vivo = true;
        ops.getPass(negocio, paseId)
            .then((p) => {
                if (!vivo) {
                    return;
                }
                setCliente(p.customer_id ?? null);
                if (p.customer_id != null) {
                    ops.listDoorAssessments(negocio, p.customer_id, { limit: 1 })
                        .then((l) => { if (vivo) setVigente(l.data[0] ?? null); })
                        .catch(() => undefined);
                }
            })
            .catch(() => { if (vivo) setCliente(null); });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, paseId]);
    const guardar = () => {
        if (cliente == null || nivel === null) {
            return;
        }
        setEstado('guardando');
        ops.createDoorAssessment(negocio, cliente, { level: nivel, ...(nivel === 'green' || motivo === null ? {} : { reason_code: motivo }), ...(nota.trim() === '' ? {} : { reason_note: nota.trim() }), pass_ref: paseId })
            .then((creada) => {
                setEstado('hecho');
                onGuardada?.({ nivel, motivo: nivel === 'green' ? null : motivo, caduca: creada?.expires_at ?? null });
            })
            .catch(() => { setEstado('error'); });
    };
    const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', ...(zona === null ? {} : { timeZone: zona }) });
    return (
        <Hoja titulo={nombre === null ? T_PUERTA.evaluacion : T_PUERTA.evaluacionDe(pila(nombre))} onCerrar={onCerrar}>
            {cliente === undefined ? (
                <p>{T_PUERTA.comprobando}</p>
            ) : cliente === null ? (
                <p>Este pase no tiene un cliente: no se puede evaluar.</p>
            ) : estado === 'hecho' ? (
                <p role="status">Evaluación guardada.</p>
            ) : (
                <>
                    {vigente !== null && <p>{T_PUERTA.vigente(vigente.level === 'red' ? 'Rojo' : 'Ámbar', fecha(vigente.expires_at))}</p>}
                    <div className="rv-pt-hoja__niveles" role="radiogroup" aria-label="Nivel">
                        {NIVELES_EVALUACION.map(([k, palabra, sub, icono]) => (
                            <button key={k} type="button" role="radio" aria-checked={nivel === k} className={`rv-pt-nivel rv-pt-nivel--${k}`} onClick={() => { setNivel(k); }}>
                                <Icono nombre={icono} />
                                <strong>{palabra}</strong>
                                <small>{sub}</small>
                            </button>
                        ))}
                    </div>
                    {nivel !== null && nivel !== 'green' && (
                        <div className="rv-pt-hoja__presets" role="radiogroup" aria-label="Motivo">
                            {MOTIVOS_EVALUACION.map(([k, t]) => <button key={k} type="button" role="radio" aria-checked={motivo === k} onClick={() => { setMotivo(k); }}>{t}</button>)}
                        </div>
                    )}
                    {nivel !== null && nivel !== 'green' && (
                        <label className="rv-pt-hoja__campo">
                            <span>Nota (opcional)</span>
                            <textarea value={nota} maxLength={200} onChange={(e) => { setNota(e.target.value); }} />
                            <small>{T_PUERTA.notaEvaluacion}</small>
                        </label>
                    )}
                    {estado === 'error' && <p role="alert" className="rv-tablet__error"><Icono nombre="circle-alert" />No he podido guardar la evaluación.</p>}
                    <div className="rv-pt-hoja__botones">
                        <button type="button" className="rv-pt__b rv-pt__b--claro" onClick={onCerrar}><Icono nombre="x" />{T_PUERTA.cancelar}</button>
                        <button type="button" className="rv-pt__b rv-pt__b--rojo" disabled={nivel === null || (nivel !== 'green' && motivo === null) || estado === 'guardando'} onClick={guardar}><Icono nombre="check" />{T_PUERTA.guardar}</button>
                    </div>
                </>
            )}
        </Hoja>
    );
}

/** Conexión y copia de la tablet: «Descargar la copia» con la parte del cupo de taquilla que pide esta tablet (G4). */
function VentanaCopia({ conConexion, hayCopia, ultima, hasta, pendientes, ocupada, error, puedeDescargar, onDescargar, onCerrar }: { conConexion: boolean; hayCopia: boolean; ultima: string; hasta: string | null; pendientes: number; ocupada: boolean; error: string | null; puedeDescargar: boolean; onDescargar: (cupo: number) => void; onCerrar: () => void }) {
    const [cupo, setCupo] = useState('0');
    return (
        <Hoja
            titulo={hayCopia ? 'Conexión y copia de la tablet' : 'Copia para trabajar sin conexión'}
            sub={hayCopia ? (conConexion ? 'Con conexión la copia se actualiza cada minuto. Sin conexión sigues validando con ella hasta 24 h después de la última conexión, y las llegadas se envían al volver.' : 'Las llegadas se guardan y se envían solas cuando vuelve la conexión.') : T_PUERTA.sinCopiaTexto}
            onCerrar={onCerrar}
        >
            <dl className="rv-pt__cifras rv-pt__cifras--filas">
                <div><dt>Conexión</dt><dd>{conConexion ? T_PUERTA.conConexion : T_PUERTA.sinConexion}</dd></div>
                <div><dt>Copia sin conexión</dt><dd>{hayCopia ? 'Lista y cifrada' : 'No descargada'}</dd></div>
                <div><dt>Última conexión</dt><dd>{ultima}</dd></div>
                {hasta !== null && <div><dt>Puedes seguir validando hasta</dt><dd>{hasta}</dd></div>}
                <div><dt>Llegadas por enviar</dt><dd>{pendientes}</dd></div>
            </dl>
            {!hayCopia && puedeDescargar && (
                <label className="rv-pt-hoja__campo">
                    <span>{T_PUERTA.cupoDeLaTablet}</span>
                    <input type="number" inputMode="numeric" min={0} value={cupo} onChange={(e) => { setCupo(e.target.value); }} />
                </label>
            )}
            {error !== null && <p role="alert" className="rv-tablet__error"><Icono nombre="circle-alert" />{error}</p>}
            <div className="rv-pt-hoja__botones">
                <button type="button" className="rv-pt__b rv-pt__b--claro" onClick={onCerrar}><Icono nombre="x" />{T_PUERTA.cerrar}</button>
                {!hayCopia && puedeDescargar && (
                    <button type="button" className="rv-pt__b rv-pt__b--rojo" disabled={ocupada} onClick={() => { onDescargar(Math.max(0, Number.parseInt(cupo, 10) || 0)); }}>
                        <Icono nombre={ocupada ? 'loader-circle' : 'download'} className={ocupada ? 'rv-gira' : undefined} />
                        {T_PUERTA.descargar}
                    </button>
                )}
            </div>
        </Hoja>
    );
}
